/**
 * The one place that talks HTTP. Every screen goes through `apiFetch`, never
 * through `fetch` directly — that is what lets us send the session cookie,
 * turn a non-2xx response into a typed `ApiError`, and keep the "show the
 * API's own message" rule (see task brief) in a single spot.
 *
 * There is no credential in this file any more: access is single sign-on
 * (document/10 RF-60) and the session lives in an `HttpOnly` cookie on the
 * API's origin, which `credentials: 'include'` is what sends.
 */
import type { ErrorBody } from '@cms/contracts'
import { tenantHeaders } from './tenant-selection.js'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly body: ErrorBody

  constructor(status: number, body: ErrorBody) {
    // The API writes its messages to be read by a person or a model (see
    // document/25-api-e-mcp.md §5) — we surface `body.message` verbatim
    // rather than inventing our own "something went wrong".
    super(body.message)
    this.status = status
    this.code = body.error
    this.body = body
  }
}

/**
 * The answers that mean "the client picked in this browser is not yours (any
 * more)": a membership revoked, a tenant id mangled in storage. The workspace
 * goes back to the home tenant instead of failing every call.
 */
export function isTenantSelectionError(error: unknown): boolean {
  return error instanceof ApiError && (error.code === 'tenant_access_denied' || error.code === 'tenant_header_invalid')
}

/** Fired on `window` when an authenticated call answers 401. */
export const SESSION_EXPIRED_EVENT = 'cms:session-expired'

/** Raised when `fetch` itself cannot reach the API — no response, no status. */
export class NoSessionError extends Error {}

export function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? 'https://api-cms.miglioresoftware.com'
}

export interface ApiFetchOptions {
  method?: string
  query?: Record<string, string | number | undefined>
  body?: unknown
  /** Forwarded as `Idempotency-Key`. Required by convention on every create/write per doc 25 §3. */
  idempotencyKey?: string
  /** Skip sending the session cookie — only `/health` needs this. */
  skipAuth?: boolean
}

function buildUrl(path: string, query?: ApiFetchOptions['query']): string {
  const url = new URL(path.replace(/^\//, ''), apiBaseUrl() + '/')
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value))
    }
  }
  return url.toString()
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json; charset=utf-8' }
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey
  // The client picked in the top bar travels with every authenticated call,
  // here and nowhere else (tenant-selection.ts). `/health` has no tenant.
  if (!options.skipAuth) Object.assign(headers, tenantHeaders())

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      // The SSO session cookie. The interface and the API are two origins of
      // the same site, so the cookie stays `SameSite=Lax` and still travels.
      credentials: options.skipAuth ? 'omit' : 'include',
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    })
  } catch {
    throw new NoSessionError(
      `Impossibile contattare l'API (${apiBaseUrl()}). Controlla la connessione o l'indirizzo configurato.`,
    )
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const data = text.length > 0 ? (JSON.parse(text) as unknown) : undefined

  if (!response.ok) {
    const body: ErrorBody =
      data && typeof data === 'object' && 'error' in (data as object)
        ? (data as ErrorBody)
        : { error: 'unknown', message: `La richiesta è fallita (${response.status}).` }
    // A 401 on an authenticated call means the session is over: the screen
    // cannot fix that, the session provider can (it sends the person to the
    // single sign-on and back to the same page).
    if (response.status === 401 && !options.skipAuth) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
    }
    throw new ApiError(response.status, body)
  }

  return data as T
}

/** A fresh key for the `Idempotency-Key` header, one per user-initiated write attempt. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

export interface ApiUploadOptions {
  idempotencyKey?: string
  /** Bytes sent so far and the total, as the browser reports them. */
  onProgress?: (loaded: number, total: number) => void
}

/**
 * The one multipart write of the API (`POST /sites/{s}/media`): a 10 MB image
 * in base64 inside JSON would be 13.3 MB for nothing, so the file travels as
 * `multipart/form-data` with `reason` as a form field. `XMLHttpRequest` and
 * not `fetch` because only it reports upload progress; everything else —
 * session cookie, `Idempotency-Key`, `ApiError` with the API's own message,
 * the 401 event — behaves exactly like `apiFetch`.
 */
export function apiUpload<T>(path: string, form: FormData, options: ApiUploadOptions = {}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('POST', buildUrl(path))
    request.withCredentials = true
    // No Content-Type: the browser writes it, with the multipart boundary.
    if (options.idempotencyKey) request.setRequestHeader('Idempotency-Key', options.idempotencyKey)
    for (const [name, value] of Object.entries(tenantHeaders())) request.setRequestHeader(name, value)
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) options.onProgress?.(event.loaded, event.total)
    }
    request.onerror = () => reject(new NoSessionError(
      `Impossibile contattare l'API (${apiBaseUrl()}). Controlla la connessione o l'indirizzo configurato.`,
    ))
    request.onload = () => {
      let data: unknown
      try {
        data = request.responseText ? JSON.parse(request.responseText) as unknown : undefined
      } catch {
        data = undefined
      }
      if (request.status >= 200 && request.status < 300) {
        resolve(data as T)
        return
      }
      const body: ErrorBody = data && typeof data === 'object' && 'error' in (data as object)
        ? data as ErrorBody
        : { error: 'unknown', message: `La richiesta è fallita (${request.status}).` }
      if (request.status === 401) window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
      reject(new ApiError(request.status, body))
    }
    request.send(form)
  })
}
